import { Location } from '@angular/common';
import {
    Component,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    FormControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
    DialogConfirm,
    DialogConfirmData,
} from 'app/dialogs/confirm/dialog-confirm';
import { formatDate } from 'app/helpers/formatDate';
import { InlineBase64UploadAdapter } from 'app/helpers/inlineBase64UploadAdapter';
import { sortCompare } from 'app/helpers/sortCompare';
import { toAzureDevOpsStateKey } from 'app/helpers/stateKey';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListAssistTypesConfigRes } from 'app/interfaces/responses/List/listAssistTypesConfigRes';
import { ListAzureDevOpsWorkItemsRes } from 'app/interfaces/responses/List/listAzureDevOpsWorkItemsRes';
import { ReadAssistsConfigRes } from 'app/interfaces/responses/Read/readAssistsConfigRes';
import { AzureService } from 'app/services/api/azure/azure.service';
import { ConfigsService } from 'app/services/api/configs/configs.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { AZURE_STATES } from 'common/constants/azure-states.constants';

@Component({
    selector: 'app-assists-azure',
    templateUrl: './assists-azure.component.html',
    styleUrls: ['./assists-azure.component.scss'],
})
export class AssistAzureComponent implements OnInit {
    @ViewChild('sentinel', { static: false })
    sentinel!: ElementRef;
    formEnabled = true;
    form: UntypedFormGroup;
    userDetails: ExposedReadUserDetailsRes = null;
    reqId: number;
    files: File[] = [];
    loaded: boolean = false;
    showAlert = false;
    alertTitle = '';
    alertHTML = '';
    alertFooter =
        'Vinsamlegast athugaðu. Þú getur notast við formið hér að neðan og beiðnin verður meðhöndluð þegar opnun hefur átt sér stað.';
    dateNow = new Date();

    selectedPanel: string = '';
    @Input() selectedPanelIndex: number = 0;

    private observer!: IntersectionObserver;
    orgRequestsColumns: string[] = [
        'workItemId',
        'title',
        'timeStamp',
        'state',
    ];
    workItems: Array<ListAzureDevOpsWorkItemsRes> = [];
    pageSize: number = 50;
    nextPageToken: string | null = null;
    isLast: boolean = false;
    isLoading: boolean = false;
    initialFetchComplete: boolean = false;
    displayedData: MatTableDataSource<ListAzureDevOpsWorkItemsRes> =
        new MatTableDataSource();

    listAssistTypes: Array<ListAssistTypesConfigRes>;
    assistTypes: Array<DropDownItem> = [];
    selectedAssistType: ListAssistTypesConfigRes;
    selectedAssistConfig: ReadAssistsConfigRes;
    submitted: boolean = false;

    descriptionEditor = ClassicEditor;
    descriptionTextHTML: string = '';

    constructor(
        private translationService: TranslationService,
        private dialog: MatDialog,
        private configsService: ConfigsService,
        private loaderService: LoaderService,
        private location: Location,
        private formBuilder: UntypedFormBuilder,
        private azureService: AzureService,
        public lsrauthService: LsrauthService,
    ) {
        this.lsrauthService.userDetails.subscribe(
            (userDetails) => (this.userDetails = userDetails),
        );
    }

    toAzureDevOpsStateKey = toAzureDevOpsStateKey;

    ngOnInit(): void {
        this.resetAndLoad();
        this.findCorrectTabBasedOnUrl();
        this.initPanelBasedOnUrl();
    }

    ngAfterViewInit(): void {
        this.configsService.listAssistTypesConfig().subscribe((res) => {
            this.listAssistTypes = res;
            this.assistTypes = this.listAssistTypes.map((x) => ({
                value: x.assistsTypesConfigId.toString(),
                viewValue: x.name,
            }));
            this.form = this.formBuilder.group({
                title: new UntypedFormControl('', [Validators.required]),
                description: new UntypedFormControl(''),
                assistsConfigId: new FormControl(null, [Validators.required]),
                // Makes sure to have nothing selected if value 'AssistTypeName' was inserted as null
                assistTypesConfigId: new FormControl(null, [
                    Validators.required,
                ]),
            });
            this.initForm();
        });
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (this.nextPageToken && !this.isLoading) {
                        this.observer.unobserve(entry.target);
                        this.fetchNextPage();
                    } else if (this.isLast) {
                        this.observer.disconnect();
                    }
                }
            });
        });
    }

    resetObserver() {
        if (this.observer) {
            this.observer.disconnect();
        }
        this.observer.observe(this.sentinel.nativeElement);
    }

    resetAndLoad() {
        this.workItems = [];
        this.nextPageToken = null;
        this.isLast = false;
        this.initialFetchComplete = false;
        this.fetchNextPage();
    }

    checkOpeningHours(assistConfig: ReadAssistsConfigRes) {
        let _dayOpenTo = new Date(assistConfig.dayOpenTo);
        let _dayOpenFrom = new Date(assistConfig.dayOpenFrom);
        if (
            new Date(assistConfig.closedTo) > this.dateNow &&
            assistConfig.closedActive
        ) {
            this.alertTitle = 'Lokun';
            this.alertHTML = assistConfig.closedTextHTML;
            this.showAlert = true;
        } else if (
            assistConfig.dayOpenActive &&
            (this.dateNow.getHours() < _dayOpenFrom.getHours() ||
                this.dateNow.getHours() > _dayOpenTo.getHours())
        ) {
            this.alertTitle = 'Opnunartími';
            this.alertHTML = assistConfig.dayOpenTextHTML;
            this.showAlert = true;
        }
    }

    formatDate(date) {
        return formatDate(date);
    }

    onChangeAssistType(assistsTypesConfigId) {
        this.loaderService.changeLoaderMessage('Kanna opnunartíma');
        this.form.get('assistTypesConfigId').setValue(assistsTypesConfigId);
        this.selectedAssistType = this.listAssistTypes.find(
            (x) => x.assistsTypesConfigId == assistsTypesConfigId,
        );
        this.initForm(
            this.listAssistTypes.find(
                (x) => x.assistsTypesConfigId == assistsTypesConfigId,
            ).name,
        );
    }

    initForm(AssistTypeName = null) {
        // Set default if nothing has been selected.
        if (AssistTypeName == null) {
            AssistTypeName = 'Innanhúss tölvuaðstoð';
            this.clearForm();
        }
        this.submitted = false;
        this.showAlert = false;
        this.formEnabled = false;
        this.descriptionEditor = ClassicEditor;
        this.configsService
            .readAssistsConfig({ AssistTypeName: AssistTypeName }, true, true)
            .subscribe((readAssistRes) => {
                this.selectedAssistType = this.listAssistTypes.find(
                    (x) => x.name == AssistTypeName,
                );
                this.selectedAssistConfig = readAssistRes;

                this.form
                    .get('assistsConfigId')
                    .setValue(readAssistRes.assistsConfigId);
                this.form
                    .get('assistTypesConfigId')
                    .setValue(
                        this.selectedAssistType.assistsTypesConfigId.toString(),
                    );

                this.formEnabled = true;
                this.checkOpeningHours(readAssistRes);
            });
    }

    clearForm = () => {
        this.form.setValue({
            title: '',
            description: '',
            assistsConfigId: null,
            assistTypesConfigId: null,
        });
        this.descriptionTextHTML = '';
        this.files = [];
        this.form.reset();
    };

    revertAllChanges() {
        if (confirm('Ertu viss um að þú viljir hætta við?')) {
            this.form.markAsPristine();
            this.initForm();
        }
    }

    public descriptionOnChange({ editor }: ChangeEvent) {
        this.form.markAsDirty();
        this.descriptionTextHTML = editor.getData();
        this.form.get('description').setValue(editor.getData());
    }

    closeAlert() {
        this.showAlert = false;
        this.form.enable();
    }

    public onEditorReady(editor: any) {
        editor.plugins.get('FileRepository').createUploadAdapter = (
            loader: any,
        ) => new InlineBase64UploadAdapter(loader);
    }

    public editorConfig: any = {
        alignment: { options: ['left', 'right', 'center', 'justify'] },
        toolbar: [
            'heading',
            '|',
            'bold',
            'italic',
            'bulletedList',
            'numberedList',
            'link',
            'imageUpload',
            '|',
            'undo',
            'redo',
        ],
        image: { toolbar: ['toggleImageCaption', 'imageTextAlternative'] },
    };

    badgeClass(state?: string) {
        const received = new Set([
            AZURE_STATES.NEW,
            AZURE_STATES.NEXT_UP,
            AZURE_STATES.NOT_STARTED,
        ]);
        const done = new Set([AZURE_STATES.CLOSED, AZURE_STATES.RESOLVED]);
        const key = toAzureDevOpsStateKey(state);

        if (received.has(key))
            return 'bg-orange-200 text-orange-800 dark:bg-orange-600 dark:text-orange-50';
        if (done.has(key))
            return 'bg-green-200 text-green-800 dark:bg-green-600 dark:text-green-50';
        return 'bg-blue-200 text-blue-800 dark:bg-blue-600 dark:text-blue-50';
    }

    //#region Attachements

    onSelect(event) {
        this.files.push(...event.addedFiles);
    }

    onRemove(event) {
        this.files.splice(this.files.indexOf(event), 1);
    }

    //#endregion

    //#region HTTP

    save() {
        this.reqId = null;
        this.submitted = true;
        if (!this.form.valid) {
            return;
        }

        const dialogRef = this.dialog.open(DialogConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {
                message: this.translationService.translate(
                    'text_confirm_send_request',
                ),
            } as DialogConfirmData,
        });

        dialogRef.afterClosed().subscribe((confirm: boolean) => {
            if (!confirm) {
                return;
            }

            this.formEnabled = false;
            this.azureService
                .createUserStory(
                    {
                        areaPath: this.selectedAssistType.azureAreaPath,
                        assistTypeName: this.selectedAssistType.name,
                        description: this.form.get('description').value,
                        iterationPath: null,
                        title: this.form.get('title').value,
                    },
                    this.files,
                )
                .subscribe((reqId) => {
                    if (reqId) {
                        this.resetAndLoad();
                        this.initForm();
                        this.reqId = reqId;
                        this.selectedPanelIndex = 1;
                    }
                    this.submitted = false;
                    this.formEnabled = true;
                });
        });
    }

    fetchNextPage() {
        if (this.isLoading || this.isLast) return;
        this.isLoading = true;

        this.azureService
            .listWorkItems(this.pageSize, this.nextPageToken)
            .subscribe({
                next: (res) => {
                    // Server already returns in correct order; merge and keep table sorted
                    this.workItems = this.workItems.concat(res.items);
                    this.workItems.sort(
                        (a, b) =>
                            new Date(b.timeStamp).getTime() -
                            new Date(a.timeStamp).getTime(),
                    );

                    this.displayedData.data = this.workItems;

                    this.isLast = res.isLast;
                    this.nextPageToken = res.nextPageToken;
                    this.isLoading = false;
                    this.initialFetchComplete = true;

                    this.resetObserver();
                },
                error: () => {
                    this.isLoading = false;
                },
            });
    }

    onLoadMore() {
        this.fetchNextPage();
    }

    //#endregion

    //#region Table Sort and Filter

    searchFilter(filterValue: string) {
        filterValue = filterValue.trim().toLocaleLowerCase(); // Remove whitespace and cast to lower case
        this.displayedData.filter = filterValue;

        if (this.displayedData.paginator) {
            this.displayedData.paginator.firstPage();
        }
    }

    sortData(sort: Sort) {
        const data = this.displayedData.data.slice();
        if (!sort.active || sort.direction === '') {
            this.displayedData.data = data;
            return;
        }

        this.displayedData.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'workItemId':
                    return sortCompare(a.workItemId, b.workItemId, isAsc);
                case 'title':
                    return sortCompare(a.title, b.title, isAsc);
                case 'timeStamp':
                    return sortCompare(a.timeStamp, b.timeStamp, isAsc);
                case 'state':
                    return sortCompare(a.state, b.state, isAsc);
                default:
                    return 0;
            }
        });
    }

    //#endregion

    // Makes sure back and forward works within tabs
    @HostListener('window:popstate', ['$event'])
    onPopState(event) {
        window.location.href = event.srcElement.location.href;
    }

    findCorrectTabBasedOnUrl() {
        try {
            this.selectedPanel = window.location.href.split('/').pop();
        } catch {}
    }

    initPanelBasedOnUrl() {
        if (this.selectedPanel == 'minar-beidnir') {
            this.selectedPanelIndex = 1;
            this.resetAndLoad();
        } else {
            this.selectedPanel = 'senda-beidni';
            this.selectedPanelIndex = 0;
        }
    }

    tabChanged(event: MatTabChangeEvent) {
        if (event.index == 0) {
            this.location.go(
                '/atvik-og-beidnir/tolvuadstod-og-breytingabeidnir/senda-beidni',
            );
            if (this.form.get('assistTypesConfigId').value == 0) {
                this.initForm();
            }
        } else if (event.index == 1) {
            this.location.go(
                '/atvik-og-beidnir/tolvuadstod-og-breytingabeidnir/minar-beidnir',
            );
            this.resetAndLoad();
        }
    }

    //#endregion
}
